import { createRoot } from "react-dom/client";
import { BrowserRouter as Router } from "react-router-dom";
import "common/scss/index.scss";
import { App, AppUpdatePrompt, appStore } from "./app";
import * as serviceWorker from "./serviceWorker";
import { Provider } from "react-redux";
import { ErrorBoundary, VersionFooter } from "common/components";
import { installGlobalErrorHandlers } from "common/services/GlobalErrorHandlers";
import { installStaleBundleHandler } from "common/services/StaleBundleHandler";
import { bootVersionGuard } from "common/services/BootVersionGuard";
import "typeface-open-sans";
import "typeface-barlow";

installGlobalErrorHandlers(appStore);
// Recovers a tab still running a pre-deploy index.html whose chunks have gone away.
installStaleBundleHandler();

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root container '#root' not found");
}
const root = createRoot(container);

// Boot gate: if this tab started from a cached index.html that a deploy has since replaced,
// reload now — before React, and before the auth state machine takes its first step. Awaiting
// it costs one small request (bounded to 1.2s) and buys an automatic recovery that needs no
// user action at all. When it returns true a reload is already in flight, so mounting would
// only render a tree that is about to be thrown away. See docs/CACHE_BUSTING.md.
const boot = async () => {
  if (await bootVersionGuard()) return;

  root.render(
    <ErrorBoundary>
      <Router>
        <Provider store={appStore}>
          <App />
          {/* Rendered at the root so the build stamp and the "new version" prompt are present
              on every surface, including the login and error screens App returns early for. */}
          <AppUpdatePrompt />
          <VersionFooter />
        </Provider>
      </Router>
    </ErrorBoundary>,
  );
};

void boot();

// Deliberately left unregistered. A service worker is the wrong tool for QQT's cache
// problem: the app is CDN-hosted and always online, and Vite already content-hashes every
// asset, so correct Cache-Control headers plus the /version.json check above cover it. A SW
// would add a second, harder-to-invalidate cache layer that can pin users to a stale build
// with no way to reach them. This call also actively evicts the CRA-era service worker from
// browsers that registered one before the Vite migration. See docs/CACHE_BUSTING.md.
serviceWorker.unregister();
